import {Store} from "../Store";
import {websocketMessageReceived, websocketConnected, websocketDisconnected, websocketConnection} from '../redux/WebSocket';

interface WebSocketManagerOptions {
    store: any;
    clientId: string | null;
    maxRetries?: number;
    retryInterval?: number;
}

class WebSocketManager {
    private static instance: WebSocketManager;
    private ws: WebSocket | null = null;
    private store: any;
    private clientId: string | null;
    private isConnected: boolean = false;
    private retryCount: number = 0;
    private maxRetries: number;
    private retryInterval: number;
    private reconnectTimeout: number | null = null;

    private constructor(options: WebSocketManagerOptions) {
        this.store = options.store;
        this.clientId = options.clientId;
        this.maxRetries = options.maxRetries ?? 10; // Default max retries
        this.retryInterval = options.retryInterval ?? 1000; // Default retry interval in ms
        this.connect();
    }

    public static getInstance(options: WebSocketManagerOptions): WebSocketManager {
        if (!WebSocketManager.instance) {
            WebSocketManager.instance = new WebSocketManager(options);
        }
        return WebSocketManager.instance;
    }

    private connect() {
        const wsURL = process.env.REACT_APP_WEBSOCKET;
        if (!wsURL) {
            console.error('WebSocket URL is not defined in environment variables.');
            return;
        }

        this.ws = new WebSocket(wsURL);

        this.ws.onopen = this.handleOpen.bind(this);
        this.ws.onmessage = this.handleMessage.bind(this);
        this.ws.onclose = this.handleClose.bind(this);
        this.ws.onerror = this.handleError.bind(this);
    }

    private handleOpen() {
        this.isConnected = true;
        this.retryCount = 0;
        this.store.dispatch(websocketConnected());

        // Register the client
        const registerMessage = {
            type: 'register',
            id: this.clientId,
        };
        this.sendMessage(registerMessage);
    }

    private handleMessage(event: MessageEvent) {
        try {
            const message = JSON.parse(event.data);
            this.store.dispatch(websocketMessageReceived(message));
        } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
        }
    }

    private handleClose() {
        this.isConnected = false;
        this.store.dispatch(websocketDisconnected());
        this.attemptReconnect();
    }

    private handleError(error: Event) {
        console.error('WebSocket encountered error:', error);
        // The onclose event will be triggered after this, so reconnection is handled there
    }

    private attemptReconnect() {
        if (this.retryCount < this.maxRetries) {
            const timeout = this.retryInterval * Math.pow(2, this.retryCount); // Exponential backoff
            console.log(`Attempting to reconnect in ${timeout} ms...`);
            this.retryCount += 1;

            this.reconnectTimeout = window.setTimeout(() => {
                this.connect();
            }, timeout);
        } else {
            console.error('Max reconnection attempts reached. Giving up.');
        }
    }

    public sendMessage(message: any) {
        if (this.ws && this.isConnected) {
            this.ws.send(JSON.stringify(message));
        } else {
            console.warn('Cannot send message. WebSocket is not connected.');
        }
    }

    public close() {
        if (this.ws) {
            this.ws.close();
        }
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        WebSocketManager.instance = null as any; // Allow reinitialization if needed
    }
}

export const setupWebSocket = (store: any, clientId: string | null, options?: { maxRetries?: number; retryInterval?: number }) => {
    return WebSocketManager.getInstance({
        store,
        clientId,
        maxRetries: options?.maxRetries,
        retryInterval: options?.retryInterval,
    });
};
